import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, inject, signal, computed, effect, OnInit, DestroyRef, OnDestroy } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { BigFormService, CustomerDetailConfig, DEFAULT_TENANT_DETAIL_CONFIG, VoucherFieldConfig, getVoucherFormConfig, AdditionalIdentity, buildAdditionalIdentityFields } from '@flexus/core';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface VoucherTypeInfo {
	id: number;
	name: string;
	description?: string | null;
	claim_type?: number | null;
	skill?: number | null;
	created?: string | null;
	modified?: string | null;
	clients?: number[];
}

interface VoucherProductTemplate {
	title?: string | null;
	make?: string | null;
	type?: string | null;
	sku?: string | null;
	sales_order_number?: string | null;
	barcode?: string | null;
	qty?: number | string | null;
	price?: number | string | null;
	agent?: string | null;
	skill?: number | null;
	mid?: string | number | null;
	active?: boolean | null;
	items?: unknown[];
	meta_data?: Record<string, unknown> | null;
	product_terms_text?: string | null;
	product_terms_version?: string | null;
}

interface VoucherTermsAndConds {
	exclusions: string;
	full_text: string;
	id: number;
	inclusions: string;
	version: string;
	voucher_type: string;
}

export interface VoucherType {
	id: number;
	name: string;
	description: string;
	client: number;
	client_value: string | number;
	discount: string | number;
	display_image: string;
	sp_value: string | number;
	summary_image: string;
	total_value: string | number;
	voucher_tags: string;
	voucher_type: VoucherTypeInfo | null;
	product_template: VoucherProductTemplate;
	terms_and_conds: VoucherTermsAndConds;
	
}

@Component({
	selector: 'flx-voucher-types',
	templateUrl: './voucher-types.component.html',
	styleUrls: ['./voucher-types.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class VoucherTypesComponent implements OnInit, OnDestroy {
	// Dependencies using inject()
	private readonly cd = inject(ChangeDetectorRef);
	public readonly bf = inject(BigFormService);
	private readonly destroyRef = inject(DestroyRef);
	
	// Store subscriptions for cleanup
	private voucherTypesSubscription?: Subscription;
	private storesSubscription?: Subscription;
	
	// Search - using signals
	readonly searchTerm = signal('');
	private readonly isUpdatingSelection = signal(false);
	
	// List state - using signals
	readonly isListCollapsed = signal(false);
	
	// Voucher data - using signals with default data
	// private readonly defaultVoucherTypes: VoucherType[] = [
	// 	{ id: 1, name: 'Carpet Tile Installation', articleNumber: '1267654', sku: '76524', price: 960 },
	// 	{ id: 2, name: 'Artificial Grass', articleNumber: '1267393', sku: '76523', price: 1500 },
	// 	{ id: 3, name: 'Install Rainwater Harv Tank No Slab', articleNumber: '1267393', sku: '76523', price: 1500 },
	// 	{ id: 4, name: 'Install Rainwater Harv Tank No Slab', articleNumber: '1267393', sku: '76523', price: 1200 },
	// 	{ id: 5, name: 'Artisan Shed 9 x 7', articleNumber: '1267393', sku: '75522', price: 2500 },
	// 	{ id: 6, name: 'Artisan Shed 9 x 11', articleNumber: '1267393', sku: '76521', price: 3600 },
	// 	{ id: 7, name: 'Artificial Grass', articleNumber: '1267393', sku: '76523', price: 1500 },
	// 	{ id: 8, name: 'Artificial Grass', articleNumber: '1267393', sku: '76523', price: 1500 },
	// 	{ id: 9, name: 'Artificial Grass', articleNumber: '1267393', sku: '76523', price: 1500 }
	// ];
	
	readonly voucherTypes = signal<VoucherType[]>([]);
	readonly stores = signal<Array<{ value: number | string; display: string; location?: string | null }>>([]);
	
	readonly selectedVoucher = signal<VoucherType | null>(null);

	private readonly termsFallback = 'Please select a voucher to view the associated terms and conditions.';

	private ensureRootControl(name: string): void {
		if (!this.bf.bigForm.get(name)) {
			this.bf.bigForm.addControl(name, new UntypedFormControl(null));
		}
	}

	private buildWhatMattersFromTerms(terms?: VoucherTermsAndConds | null): string | null {
		const parts: string[] = [];
		if (typeof terms?.inclusions === 'string' && terms.inclusions.trim()) {
			parts.push('Included\r\n' + terms.inclusions.trim());
		}
		if (typeof terms?.exclusions === 'string' && terms.exclusions.trim()) {
			parts.push('Exclude\r\n' + terms.exclusions.trim());
		}
		return parts.length > 0 ? parts.join('\r\n\r\n') : null;
	}

	private buildTermsAndConditionsFromTerms(terms?: VoucherTermsAndConds | null): string {
		if (!terms) {
			return this.termsFallback;
		}
		const parts: string[] = [];
		if (typeof terms.inclusions === 'string' && terms.inclusions.trim()) {
			parts.push('Included\r\n' + terms.inclusions.trim());
		}
		if (typeof terms.exclusions === 'string' && terms.exclusions.trim()) {
			parts.push('Excluded\r\n' + terms.exclusions.trim());
		}
		if (typeof terms.full_text === 'string' && terms.full_text.trim()) {
			parts.push('Full Terms and Conditions\r\n' + terms.full_text.trim());
		}
		return parts.length > 0 ? parts.join('\r\n\r\n') : this.termsFallback;
	}

	private applyVoucherSelection(voucher: VoucherType, job: UntypedFormGroup | null): void {
		if (!job || !this.bf.bigForm) {
			return;
		}
		const ensureJobControl = (name: string) => {
			if (!job.get(name)) {
				job.addControl(name, new UntypedFormControl(null));
			}
		};
		this.ensureRootControl('claim_class');
		this.ensureRootControl('whatmatters_from_voucher');
		this.ensureRootControl('terms_and_conditions');
		this.ensureRootControl('voucher_client_value');
		this.ensureRootControl('voucher_sp_value');
		this.ensureRootControl('voucher_total_value');
		ensureJobControl('voucher_terms');

		const terms = voucher.terms_and_conds ?? null;

		const toNum = (val: string | number | null | undefined): number | null => {
			if (val == null) return null;
			const n = Number(val);
			return Number.isFinite(n) ? n : null;
		};

		job.patchValue({
			voucher_id: voucher.id,
			voucher_type: voucher.voucher_type ?? null,
			voucher_type_id: voucher.voucher_type?.id ?? null,
			voucher_name: voucher.name ?? null,
			voucher_description: voucher.description ?? null,
			voucher_claim_type: voucher.voucher_type?.claim_type ?? null,
			voucher_skill: voucher.voucher_type?.skill ?? null,
			voucher_terms: terms,
			voucher_product_template: voucher.product_template ?? null,
			title: voucher.name ?? null,
			terms: voucher.terms_and_conds?.full_text ?? null,
			total_cost: voucher.total_value ?? null,
			quantity_available: voucher.product_template?.qty ?? null,
			delivery_distance: voucher.product_template?.meta_data?.['delivery_distance'] ?? null,
			skill_id: voucher.voucher_type?.skill ?? job.get('skill_id')?.value ?? null
		});

		this.bf.bigForm.patchValue({
			selectedClaimClass: [voucher.voucher_type?.claim_type],
			claim_class: voucher.voucher_type?.claim_type,
			whatmatters_from_voucher: this.buildWhatMattersFromTerms(terms),
			terms_and_conditions: this.buildTermsAndConditionsFromTerms(terms),
			voucher_client_value: toNum(voucher.client_value),
			voucher_sp_value: toNum(voucher.sp_value),
			voucher_total_value: toNum(voucher.total_value)
		});
	}

	// Computed signals for derived state
	readonly hasSelectedVoucher = computed(() => this.selectedVoucher() !== null);
	readonly selectedVoucherDisplayText = computed(() => {
		const voucher = this.selectedVoucher();
		if (!voucher) return '';
		// return `${voucher.name} - SKU: ${voucher.sku} - ${this.formatPrice(voucher.price)}`;
		return `${voucher.name} - SKU: ${voucher.description}`;
	});

	readonly filteredVoucherTypes = computed(() => {
		const term = this.searchTerm().trim().toLowerCase();
		const filtered = !term
			? this.voucherTypes()
			: this.voucherTypes().filter(voucher =>
			voucher.name.toLowerCase().includes(term) ||
			voucher.description.toLowerCase().includes(term) ||
			voucher.product_template?.sku?.toString().toLowerCase().includes(term) ||
			voucher.product_template?.mid?.toString().toLowerCase().includes(term)
		);
		return [...filtered].sort((a, b) => {
			const aKey = this.getVoucherSortKey(a.name);
			const bKey = this.getVoucherSortKey(b.name);
			if (aKey.order !== bKey.order) {
				return aKey.order - bKey.order;
			}
			return aKey.label.localeCompare(bKey.label, undefined, { sensitivity: 'base' });
		});
	});
	
	readonly isEmptyResults = computed(() => this.filteredVoucherTypes().length === 0);
	readonly showSearchPlaceholder = computed(() => !this.isListCollapsed() || !this.hasSelectedVoucher());

	private readonly tenantIdSignal = signal<number | null | undefined>(null);
	private readonly metadataSignal = signal<{ customer_detail_config?: CustomerDetailConfig } | null | undefined>(null);
	private readonly tenantOverridesSignal = signal<Record<string, CustomerDetailConfig> | undefined>(undefined);
	private readonly defaultTenantOverrides = this.buildDefaultTenantOverrides();
	private readonly formTenantId = signal<number | null | undefined>(null);
	private readonly formMetadata = signal<{ customer_detail_config?: CustomerDetailConfig } | null | undefined>(null);
	private readonly formAdditionalIdentities = signal<AdditionalIdentity[]>([]);

	/**
	 * Some voucher fields exist in the shared tenant config but should not be shown/used in this flow.
	 * For now, we explicitly suppress them here so the Job Details UI does not render them.
	 */
	private readonly suppressedVoucherControlNames = new Set<string>([
		// Commented out per request ("Where to collect from" field in Job Details / voucher types flow)
		'where_to_collect'
	]);

	readonly voucherFormConfig = computed(() => {
		const config = getVoucherFormConfig(
			this.tenantIdSignal() ?? this.formTenantId(),
			this.metadataSignal() ?? this.formMetadata(),
			this.tenantOverridesSignal() ?? this.defaultTenantOverrides
		);

		if (!config) {
			return config;
		}

		return {
			...config,
			fields: (config.fields ?? []).filter(field => !this.suppressedVoucherControlNames.has(field.controlName))
		};
	});
	private readonly jobValueVersion = signal(0);
	
	// Form for voucher details
	private readonly activeJobIndex = signal(0);

	readonly jobGroup = computed(() => {
		const jobs = this.bf.bigForm.get('jobs') as UntypedFormArray | null;
		const index = this.activeJobIndex();
		if (!jobs || !jobs.at(index)) {
			return null;
		}
		return jobs.at(index) as UntypedFormGroup;
	});

	readonly visibleVoucherFields = computed<VoucherFieldConfig[]>(() => {
		const config = this.voucherFormConfig();
		const job = this.jobGroup();
		this.jobValueVersion();
		if (!config?.visible || !job) {
			return [];
		}

		const baseFields = (config.fields ?? []).filter(field => {
			// Safety: suppress fields we don't want rendered in Job Details even if config still contains them.
			if (this.suppressedVoucherControlNames.has(field.controlName)) {
				return false;
			}
			if (!field.displayWhen) {
				return true;
			}
			const control = job.get(field.displayWhen.controlName);
			return control?.value === field.displayWhen.equals;
		});

		// Inject additional identity fields (e.g. membership number) at the top of the form
		const identityFields = buildAdditionalIdentityFields(this.formAdditionalIdentities());
		if (identityFields.length === 0) {
			return baseFields;
		}

		return [...identityFields, ...baseFields];
	});

	// Inputs
	@Input() voucherTypesData$?: Observable<VoucherType[]>;
	@Input() storesData$?: Observable<{ value: number | string; display: string }[]>;
	@Input() set tenantId(value: number | null | undefined) {
		this.tenantIdSignal.set(value);
	}
	@Input() set metadata(value: { customer_detail_config?: CustomerDetailConfig } | null | undefined) {
		this.metadataSignal.set(value);
	}
	@Input() set tenantOverrides(value: Record<string, CustomerDetailConfig> | undefined) {
		this.tenantOverridesSignal.set(value);
	}

	constructor() {
		// Effect: Update big form when voucher is selected
		// allowSignalWrites is needed because patchValue may trigger signal writes
		effect(() => {
			const voucher = this.selectedVoucher();
			const job = this.jobGroup();
			if (!voucher || !job || !this.bf.bigForm) return;

			this.applyVoucherSelection(voucher, job);
		}, { allowSignalWrites: true });

		effect(() => {
			const job = this.jobGroup();
			const voucherTypes = this.voucherTypes();
			const currentSelected = this.selectedVoucher();
			const voucherIdControl = job?.get('voucher_id');
			const voucherId = voucherIdControl?.value ?? null;

			if (!voucherIdControl) {
				return;
			}

			if (!voucherId) {
				if (currentSelected) {
					this.isUpdatingSelection.set(true);
					this.selectedVoucher.set(null);
					this.searchTerm.set('');
					this.isListCollapsed.set(false);
					Promise.resolve().then(() => {
						this.isUpdatingSelection.set(false);
					});
				}
				return;
			}

			const voucherIdNumber = Number(voucherId);
			const match =
				voucherTypes.find(voucher => {
					if (Number.isFinite(voucherIdNumber)) {
						return Number(voucher.id) === voucherIdNumber;
					}
					return String(voucher.id) === String(voucherId);
				}) ?? null;
			if (!match || currentSelected?.id === match.id) {
				return;
			}

			this.isUpdatingSelection.set(true);
			this.selectedVoucher.set(match);
			this.isListCollapsed.set(true);
			this.searchTerm.set(this.selectedVoucherDisplayText());
			this.applyVoucherSelection(match, job);
			Promise.resolve().then(() => {
				this.isUpdatingSelection.set(false);
			});
		}, { allowSignalWrites: true });

		effect((onCleanup) => {
			const job = this.jobGroup();
			if (!job) {
				return;
			}

			this.jobValueVersion.update(value => value + 1);
			const sub = job.valueChanges.subscribe(() => {
				this.jobValueVersion.update(value => value + 1);
			});

			onCleanup(() => sub.unsubscribe());
		}, { allowSignalWrites: true });

		effect((onCleanup) => {
			const selectedCompanyControl = this.bf.bigForm.get('selected_company');
			const tenantConfigControl = this.bf.bigForm.get('tenant_document_config');

			const setFromForm = () => {
				const selectedCompany = selectedCompanyControl?.value ?? null;
				const tenantConfig = tenantConfigControl?.value ?? null;
				const tenantId = selectedCompany ?? tenantConfig?.company_id ?? null;
				this.formTenantId.set(tenantId);
				this.formMetadata.set(tenantConfig?.metadata ?? null);
				this.formAdditionalIdentities.set(tenantConfig?.metadata?.additional_identities ?? []);
			};

			setFromForm();

			const subs = [
				selectedCompanyControl?.valueChanges.subscribe(setFromForm),
				tenantConfigControl?.valueChanges.subscribe(setFromForm)
			].filter(Boolean) as Array<{ unsubscribe: () => void }>;

			onCleanup(() => subs.forEach(sub => sub.unsubscribe()));
		}, { allowSignalWrites: true });

		// Effect: Ensure additional identity form controls exist on the active job group
		effect(() => {
			const job = this.jobGroup();
			const identities = this.formAdditionalIdentities();
			if (!job || identities.length === 0) {
				return;
			}

			for (const identity of identities) {
				const controlName = `identity_${identity.identity_type}`;
				if (!job.get(controlName)) {
					job.addControl(controlName, new UntypedFormControl(null, [Validators.required]));
				}
			}
		}, { allowSignalWrites: true });

		effect((onCleanup) => {
			const job = this.jobGroup();
			if (!job) {
				return;
			}

			const ensureControl = (name: string) => {
				if (!this.bf.bigForm.get(name)) {
					this.bf.bigForm.addControl(name, new UntypedFormControl(null));
				}
			};

			const parseLocation = (location?: string | null) => {
				if (!location) return null;
				const [latRaw, lngRaw] = String(location).split(',').map(part => part.trim());
				const lat = Number(latRaw);
				const lng = Number(lngRaw);
				if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
					return null;
				}
				return { lat, lng };
			};

			const setFromStore = () => {
				const storeValue = job.get('store_poi')?.value ?? null;
				if (!storeValue) {
					return;
				}
				const stores = this.stores();
				const selected = stores.find(store => store.value === storeValue) ?? null;
				const coords = parseLocation(selected?.location ?? null);
				if (!coords) {
					return;
				}

				ensureControl('address_from_lat');
				ensureControl('address_from_lng');
				ensureControl('address_store_poi');

				this.bf.bigForm.patchValue({
					address_from_lat: coords.lat,
					address_from_lng: coords.lng,
					address_store_poi: storeValue
				});
			};

			setFromStore();

			const sub = job.get('store_poi')?.valueChanges?.subscribe(setFromStore);
			onCleanup(() => sub?.unsubscribe());
		}, { allowSignalWrites: true });
		
	}
	
	ngOnInit(): void {
		// Set up subscription for Observable input after component initialization
		// This ensures we're outside any reactive context when subscribing
		if (this.voucherTypesData$) {
			// Unsubscribe from previous subscription if it exists
			if (this.voucherTypesSubscription) {
				this.voucherTypesSubscription.unsubscribe();
			}
			this.voucherTypesSubscription = this.voucherTypesData$
				.pipe(
					takeUntilDestroyed(this.destroyRef),
					distinctUntilChanged<VoucherType[]>((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
				)
				.subscribe(data => {
					this.voucherTypes.set(data ?? []);
				});
		}

		if (this.storesData$) {
			// Unsubscribe from previous subscription if it exists
			if (this.storesSubscription) {
				this.storesSubscription.unsubscribe();
			}
			this.storesSubscription = this.storesData$
				.pipe(
					takeUntilDestroyed(this.destroyRef),
					distinctUntilChanged<Array<{ value: number | string; display: string }>>((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
				)
				.subscribe(stores => {
					const normalized = this.normalizeStoreOptions(stores ?? []);
					this.stores.set(normalized);
				});
		}

	}

	ngOnDestroy(): void {
		// Explicitly unsubscribe from all subscriptions
		if (this.voucherTypesSubscription) {
			this.voucherTypesSubscription.unsubscribe();
		}
		if (this.storesSubscription) {
			this.storesSubscription.unsubscribe();
		}
	}

	private normalizeStoreOptions(
		stores: Array<{ value?: number | string; display?: string; id?: number | string; name?: string; location?: string | null }>
	): Array<{ value: number | string; display: string; location?: string | null }> {
		return (stores ?? [])
			.map(store => {
				const value = store?.value ?? store?.id ?? '';
				const display = store?.display ?? store?.name ?? '';
				return { value, display, location: store?.location ?? null };
			})
			.filter(store => store.value !== '' && store.display !== '');
	}

	private getVoucherSortKey(name?: string | null): { order: number; label: string } {
		const label = (name ?? '').trim();
		console.log('label', label);
		const match = /^(\d+)\s*(.*)$/.exec(label);
		if (!match) {
			return { order: Number.POSITIVE_INFINITY, label };
		}
		return {
			order: Number(match[1]),
			label: (match[2] ?? '').trim() || label
		};
	}

	private buildDefaultTenantOverrides(): Record<string, CustomerDetailConfig> {
		const defaultVoucherForm = DEFAULT_TENANT_DETAIL_CONFIG.voucherForm;
		// Tenant 3 (Game): hide what_is_being_installed in Job Details.
		const voucherFormWithoutWhatIsBeingInstalled =
			defaultVoucherForm?.fields != null
				? { ...defaultVoucherForm, fields: defaultVoucherForm.fields.filter(f => f.controlName !== 'what_is_being_installed') }
				: defaultVoucherForm;

		return {
			// Game (id: 3): default voucher config but without 'What is being installed'.
			'3': {
				...DEFAULT_TENANT_DETAIL_CONFIG,
				voucherForm: voucherFormWithoutWhatIsBeingInstalled
			},
			// Makro (id: 11) hides where_to_collect.
			// '11': {
			// 	...DEFAULT_TENANT_DETAIL_CONFIG,
			// 	voucherForm: {
			// 		...(defaultVoucherForm ?? { visible: true, fields: [] }),
			// 		fields: withoutWhereToCollect ?? []
			// 	}
			// }
		};
	}

	// Search is now handled by computed signal, no need for onSearch() method

	onSearchBarClick(): void {
		// Expand list when search bar is clicked
		if (this.isListCollapsed()) {
			// Clear the formatted display text to allow searching
			const displayText = this.selectedVoucherDisplayText();
			if (this.searchTerm() === displayText) {
			this.searchTerm.set('');
			}
			this.expandList();
		}
	}

	onSearchIconClick(): void {
		// Expand list when search icon is clicked
		if (this.isListCollapsed()) {
			// Clear the formatted display text to allow searching
			const displayText = this.selectedVoucherDisplayText();
			if (this.searchTerm() === displayText) {
			this.searchTerm.set('');
			}
			this.expandList();
		}
		// Search is handled automatically by computed signal
	}

	onSearchInput(value: string): void {
		// Skip search if we're programmatically updating the selection
		if (this.isUpdatingSelection()) {
			this.searchTerm.set(value);
			return;
		}
		
		// Check if the value matches the selected voucher display format exactly
		// If so, don't perform search (it's just the display text)
		const displayText = this.selectedVoucherDisplayText();
		if (value === displayText) {
			// This is the formatted display text, not a real search - skip search
			this.searchTerm.set(value);
			return;
		}
		
		// Update search term (computed signal will automatically filter)
		this.searchTerm.set(value);
				// When user types in search, expand list if collapsed
		if (this.isListCollapsed()) {
			this.expandList();
		}
	}

	selectVoucher(voucher: VoucherType): void {
		const job = this.jobGroup();
		if (job && this.bf.bigForm) {
			this.applyVoucherSelection(voucher, job);
		}
		this.selectedVoucher.set(voucher);
		this.isListCollapsed.set(true);
		
		// Set flag to prevent search from running when updating search term
		this.isUpdatingSelection.set(true);
		
		// Update search bar to show selected voucher (using computed signal)
		this.searchTerm.set(this.selectedVoucherDisplayText());
		
		// Reset flag after Angular change detection cycle
		Promise.resolve().then(() => {
			this.isUpdatingSelection.set(false);
		});
	}

	isSelected(voucher: VoucherType): boolean {
		return this.selectedVoucher()?.id === voucher.id;
	}

	onAddVoucher(): void {
		const voucher = this.selectedVoucher();
		const job = this.jobGroup();
		if (!voucher || !job || job.invalid) return;

		const voucherData = {
			...voucher,
			...job.value
		};

		console.log('Adding voucher:', voucherData);
	}

	/**
	 * Restricts keyboard input on fields with format 'decimal' to digits and a single decimal point.
	 * Allows navigation keys (arrows, tab, backspace, delete, home, end) and clipboard shortcuts.
	 */
	onFieldKeydown(event: KeyboardEvent, field: VoucherFieldConfig): void {
		if (field.format !== 'decimal') {
			return;
		}

		// Always allow navigation / editing keys
		const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
		if (allowedKeys.includes(event.key)) {
			return;
		}

		// Allow Ctrl/Cmd + A/C/V/X (select-all, copy, paste, cut)
		if (event.ctrlKey || event.metaKey) {
			return;
		}

		// Allow a single decimal point
		if (event.key === '.') {
			const currentValue = (event.target as HTMLInputElement)?.value ?? '';
			if (currentValue.includes('.')) {
				event.preventDefault();
			}
			return;
		}

		// Block anything that isn't a digit
		if (!/^\d$/.test(event.key)) {
			event.preventDefault();
		}
	}

	/**
	 * Handles blur events on voucher form fields.
	 * For fields with format 'decimal', ensures the value is formatted to 2 decimal places.
	 */
	onFieldBlur(field: VoucherFieldConfig): void {
		if (field.format !== 'decimal') {
			return;
		}
		const job = this.jobGroup();
		if (!job) {
			return;
		}
		const control = job.get(field.controlName);
		if (!control) {
			return;
		}
		const raw = control.value;
		const num = Number(raw);
		const formatted = Number.isFinite(num) ? num.toFixed(2) : '0.00';
		if (control.value !== formatted) {
			control.setValue(formatted, { emitEvent: false });
		}
	}

	formatPrice(price: number | string | null | undefined): string {
		const amount = typeof price === 'number' ? price : Number(price);
		if (!Number.isFinite(amount)) {
			return '';
		}
		return `R${amount.toLocaleString('en-ZA')}`;
	}

	clearSearch(): void {
		this.searchTerm.set('');
		// Filtered list is automatically updated by computed signal
	}

	expandList(): void {
		this.isListCollapsed.set(false);
		// Filtered list is automatically updated by computed signal
	}

}
